import { useState, useEffect, useCallback, type ReactNode } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import TextField from '@mui/material/TextField'
import IconButton from '@mui/material/IconButton'
import Skeleton from '@mui/material/Skeleton'
import Tooltip from '@mui/material/Tooltip'
import Collapse from '@mui/material/Collapse'
import AddIcon from '@mui/icons-material/Add'
import SearchIcon from '@mui/icons-material/Search'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import DescriptionIcon from '@mui/icons-material/Description'
import BarChartIcon from '@mui/icons-material/BarChart'
import WebIcon from '@mui/icons-material/Web'
import AppsIcon from '@mui/icons-material/Apps'
import FolderIcon from '@mui/icons-material/Folder'
import ExtensionIcon from '@mui/icons-material/Extension'
import SmartToyIcon from '@mui/icons-material/SmartToy'
import CorporateFareIcon from '@mui/icons-material/CorporateFare'
import FileUploadIcon from '@mui/icons-material/FileUpload'
import HandymanIcon from '@mui/icons-material/Handyman'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import SettingsIcon from '@mui/icons-material/Settings'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import MenuIcon from '@mui/icons-material/Menu'
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline'
import type { SessionSummary } from '@reachinsight-app/shared/src/components/R3Chat/types'
import { getMascotByName } from '@reachinsight-app/shared/src/components/R3Chat/RosMascots'
import { KNOWN_SOURCES } from '../config/environment'

// ── Types ──────────────────────────────────────────────────────────

interface AgentDefinition {
  slug: string
  displayName: string
  mascot: string
  shortDescription: string
  status: string
  category: string
  toolRoute: string | null
}

interface UploadedFile {
  key: string
  size: number
  uploaded: string
}

interface ArtifactVersionRow {
  id: string
  version: number
  type: string
  title: string
  client?: string
  sessionId: string
  publishedAt: string
  bodyR2Key: string
}

interface ArtifactItem {
  id: string
  title: string
  client?: string
  sessionId: string
  latestVersion: number
  versions: ArtifactVersionRow[]
}

interface RosSidebarProps {
  sessions: SessionSummary[]
  sessionsLoading: boolean
  activeSessionId: string | undefined
  onSelectSession: (sessionId: string) => void
  onNewChat: () => void
  onOpenHistory: () => void
  onOpenArtifactVersion: (version: ArtifactVersionRow & { bodyR2Key: string }) => void
  apiBaseUrl: string
  token: string | null
  collapsed: boolean
  onToggleCollapse: () => void
  /** PostHog 'reachy-agent-builder' flag — shows the Agents section for admins/devs */
  showAgents: boolean
  onOpenAgents: () => void
}

// ── Helpers ────────────────────────────────────────────────────────

function formatDate(timestamp: number | string): string {
  const d = new Date(timestamp)
  const now = new Date()
  const diff = now.getTime() - d.getTime()
  if (diff < 60000) return 'just now'
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

function truncate(s: string, max: number): string {
  return s.length > max ? s.slice(0, max) + '...' : s
}

// ── Component ──────────────────────────────────────────────────────

export function RosSidebar({
  sessions, sessionsLoading, activeSessionId,
  onSelectSession, onNewChat, onOpenHistory, onOpenArtifactVersion, apiBaseUrl, token,
  collapsed, onToggleCollapse, showAgents, onOpenAgents,
}: RosSidebarProps) {

  // Section collapse state
  const [sectionsOpen, setSectionsOpen] = useState<Record<string, boolean>>({
    recent: true, studioTools: true, agents: false, artifacts: false, files: false, mySkills: false, orgSkills: false,
  })
  const toggleSection = (key: string) => setSectionsOpen(prev => ({ ...prev, [key]: !prev[key] }))

  // Search
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState<SessionSummary[] | null>(null)

  // Data
  const [agents, setAgents] = useState<AgentDefinition[]>([])
  const [files, setFiles] = useState<UploadedFile[]>([])

  const headers = useCallback((): Record<string, string> => {
    const h: Record<string, string> = { 'Content-Type': 'application/json' }
    if (token) h['Authorization'] = `Bearer ${token}`
    return h
  }, [token])

  // Load agents
  useEffect(() => {
    fetch(`${apiBaseUrl}/api/agent-builder/agents`, { credentials: 'include', headers: headers() })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (Array.isArray(data?.agents)) setAgents(data.agents)
      })
      .catch(() => {})
  }, [apiBaseUrl, headers])

  // Load files
  useEffect(() => {
    fetch(`${apiBaseUrl}/api/files`, { credentials: 'include', headers: headers() })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (Array.isArray(data?.files)) setFiles(data.files)
      })
      .catch(() => {})
  }, [apiBaseUrl, headers])

  // Load published artifacts (the versioned catalog)
  const [artifactGroups, setArtifactGroups] = useState<{ type: string; items: ArtifactItem[] }[]>([])
  useEffect(() => {
    fetch(`${apiBaseUrl}/api/r3/artifacts`, { credentials: 'include', headers: headers() })
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (Array.isArray(data?.groups)) setArtifactGroups(data.groups) })
      .catch(() => {})
  }, [apiBaseUrl, headers, sessions.length]) // refetch when sessions refresh (publishes bump it)

  // Search handler
  const handleSearch = useCallback(async () => {
    if (!searchQuery.trim()) { setSearchResults(null); return }
    try {
      const res = await fetch(`${apiBaseUrl}/api/r3/sessions/search?q=${encodeURIComponent(searchQuery)}`, {
        credentials: 'include', headers: headers(),
      })
      if (!res.ok) { setSearchResults([]); return }
      const data = await res.json() as { sessions: SessionSummary[] }
      setSearchResults(data.sessions || [])
    } catch {
      setSearchResults([])
    }
  }, [searchQuery, apiBaseUrl, headers])

  // Studio-only capabilities — exist as Studio tools, not (yet) as Ros agents
  const studioTools = agents.filter(a => a.status === 'tool_available' && a.toolRoute)

  // Dispatchable agents, system first — previewed in the flag-gated Agents section
  const activeAgents = agents.filter(a => a.status === 'active')
  const topAgents = [...activeAgents].sort((a, b) =>
    a.category === b.category ? 0 : a.category === 'system' ? -1 : 1
  ).slice(0, 5)

  // Recent sessions (top 10)
  const recentSessions = sessions.slice(0, 10)

  // Filter sessions by search
  const displaySessions = searchResults !== null ? searchResults : recentSessions

  return (
    <>
    {/* Collapse/expand toggle — fixed position, vertically centered on sidebar edge */}
    <Box
      onClick={onToggleCollapse}
      sx={{
        position: 'fixed',
        left: collapsed ? 56 - 12 : 260 - 12,
        top: '50%',
        transform: 'translateY(-50%)',
        width: 24, height: 24,
        borderRadius: '50%',
        background: '#fff',
        border: '1px solid #e8e5de',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        cursor: 'pointer',
        zIndex: 1200,
        boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
        transition: 'left 0.2s ease',
        '&:hover': { background: '#f5f5f0', borderColor: '#6b8f5e' },
        '&:hover svg': { color: '#6b8f5e !important' },
      }}
    >
      {collapsed
        ? <MenuIcon sx={{ fontSize: 14, color: '#999' }} />
        : <ChevronLeftIcon sx={{ fontSize: 16, color: '#999' }} />
      }
    </Box>

    <Box sx={{
      width: collapsed ? 56 : 260,
      minWidth: collapsed ? 56 : 260,
      height: '100%',
      borderRight: '1px solid #e8e5de',
      background: '#fff',
      display: 'flex', flexDirection: 'column', overflow: 'hidden',
      transition: 'width 0.2s ease, min-width 0.2s ease',
    }}>
      {/* Header */}
      <Box sx={{ px: collapsed ? 0.5 : 1.5, py: 1.5, display: 'flex', alignItems: 'center', gap: 1, borderBottom: '1px solid #f0eeea', justifyContent: collapsed ? 'center' : 'flex-start' }}>
        {collapsed ? (
          <Tooltip title="New conversation" placement="right">
            <IconButton size="small" onClick={onNewChat} sx={{ color: '#6b8f5e' }}>
              <AddIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ) : (
          <>
            <TextField
              size="small"
              placeholder="Search..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleSearch() }}
              InputProps={{
                startAdornment: <SearchIcon sx={{ fontSize: 16, color: '#999', mr: 0.5 }} />,
                sx: { fontSize: '0.78rem', borderRadius: 2, background: '#faf9f5', '& fieldset': { borderColor: '#e8e5de' } },
              }}
              fullWidth
              sx={{ '& .MuiInputBase-input': { py: 0.6 } }}
            />
            <Tooltip title="New conversation">
              <IconButton size="small" onClick={onNewChat} sx={{ color: '#6b8f5e', flexShrink: 0 }}>
                <AddIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </>
        )}
      </Box>

      {/* Scrollable content */}
      <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
        {/* Collapsed: icon-only navigation */}
        {collapsed && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, pt: 1.5 }}>
            <Tooltip title="Recent chats" placement="right">
              <IconButton size="small" onClick={() => { onToggleCollapse(); setSectionsOpen(prev => ({ ...prev, recent: true })) }} sx={{ color: '#999' }}><ChatBubbleOutlineIcon fontSize="small" /></IconButton>
            </Tooltip>
            <Tooltip title="Studio Tools" placement="right">
              <IconButton size="small" onClick={() => { onToggleCollapse(); setSectionsOpen(prev => ({ ...prev, studioTools: true })) }} sx={{ color: '#999' }}><HandymanIcon fontSize="small" /></IconButton>
            </Tooltip>
            <Tooltip title="Artifacts" placement="right">
              <IconButton size="small" onClick={() => { onToggleCollapse(); setSectionsOpen(prev => ({ ...prev, artifacts: true })) }} sx={{ color: '#999' }}><BarChartIcon fontSize="small" /></IconButton>
            </Tooltip>
            <Tooltip title="Files" placement="right">
              <IconButton size="small" onClick={() => { onToggleCollapse(); setSectionsOpen(prev => ({ ...prev, files: true })) }} sx={{ color: '#999' }}><FolderIcon fontSize="small" /></IconButton>
            </Tooltip>
            {showAgents && (
              <Tooltip title="Agents" placement="right">
                <IconButton size="small" onClick={onOpenAgents} sx={{ color: '#999' }}><SmartToyIcon fontSize="small" /></IconButton>
              </Tooltip>
            )}
            <Tooltip title="My Skills" placement="right">
              <IconButton size="small" onClick={() => { onToggleCollapse(); setSectionsOpen(prev => ({ ...prev, mySkills: true })) }} sx={{ color: '#999' }}><ExtensionIcon fontSize="small" /></IconButton>
            </Tooltip>
          </Box>
        )}

        {/* Expanded: full content */}
        {!collapsed && <>
        {/* Recent Sessions */}
        <SidebarSection
          title="Recent"
          open={sectionsOpen.recent}
          onToggle={() => toggleSection('recent')}
          count={displaySessions.length}
        >
          {sessionsLoading ? (
            <Box sx={{ px: 2 }}>
              {[1, 2, 3].map(i => <Skeleton key={i} height={32} sx={{ mb: 0.5 }} />)}
            </Box>
          ) : displaySessions.length === 0 ? (
            <EmptyHint text={searchResults !== null ? 'No results' : 'No conversations yet'} />
          ) : (
            displaySessions.map(s => (
              <SidebarItem
                key={s.id}
                label={s.intentSummary || 'New conversation'}
                secondary={formatDate(s.startedAt)}
                active={activeSessionId === s.id}
                panelType={s.panelType}
                onClick={() => onSelectSession(s.id)}
              />
            ))
          )}
          {!sessionsLoading && searchResults === null && sessions.length > 0 && (
            <Typography
              onClick={onOpenHistory}
              sx={{
                px: 2, py: 0.75, fontSize: '0.72rem', fontWeight: 600, color: '#6b8f5e',
                cursor: 'pointer', '&:hover': { textDecoration: 'underline' },
              }}
            >
              See all →
            </Typography>
          )}
        </SidebarSection>

        {/* Studio Tools — capabilities not (yet) available as Ros agents. Clicking
            opens the corresponding tool in Studio. */}
        <SidebarSection
          title="Studio Tools"
          icon={<HandymanIcon sx={{ fontSize: 14 }} />}
          open={sectionsOpen.studioTools}
          onToggle={() => toggleSection('studioTools')}
          count={studioTools.length}
        >
          {studioTools.length === 0 ? (
            <EmptyHint text="No Studio tools" />
          ) : (
            studioTools.map(a => (
              <SidebarItem
                key={a.slug}
                label={a.displayName}
                secondary={a.shortDescription}
                trailing={<OpenInNewIcon sx={{ fontSize: 11, color: '#999' }} />}
                onClick={() => window.open(`${KNOWN_SOURCES.studio.url}${a.toolRoute}`, '_blank', 'noopener,noreferrer')}
              />
            ))
          )}
        </SidebarSection>

        {/* Artifacts — published, versioned deliverables, segregated by Client tag →
            Research Domain tag → source chat (never a flat v1/v2 soup). Version chips are
            clickable and open that exact frozen version read-only. */}
        <SidebarSection
          title="My Artifacts"
          icon={<BarChartIcon sx={{ fontSize: 14 }} />}
          open={sectionsOpen.artifacts}
          onToggle={() => toggleSection('artifacts')}
          count={artifactGroups.reduce((n, g) => n + g.items.length, 0)}
        >
          {artifactGroups.length === 0 ? (
            <EmptyHint text="Nothing published yet" />
          ) : (
            (() => {
              const sessionById = new Map(sessions.map(s => [s.id, s]))
              // Flatten all artifacts, then group by client → research_domain → source chat title.
              const buckets = new Map<string, { kind: 'client' | 'domain' | 'session'; items: { item: ArtifactItem; type: string }[] }>()
              for (const g of artifactGroups) {
                for (const item of g.items) {
                  const sess = sessionById.get(item.sessionId)
                  const client = item.client ?? sess?.tags?.client
                  const domain = sess?.tags?.research_domain
                  const key = client ? `c:${client}` : domain ? `d:${domain}` : `s:${item.sessionId}`
                  // Untagged sessions get a unique session-<id> folder (chat titles repeat too much).
                  const label = client ?? domain ?? `session-${item.sessionId.slice(0, 8)}`
                  const bucket = buckets.get(key) ?? { kind: client ? 'client' : domain ? 'domain' : 'session', items: [] }
                  bucket.items.push({ item, type: g.type })
                  buckets.set(key, bucket)
                  ;(bucket as { label?: string }).label = label
                }
              }
              // The same name can be a client folder AND a research-domain folder (e.g. one
              // session tagged client=Smartly, another domain=Smartly) — disambiguate on collision.
              const labelKinds = new Map<string, Set<string>>()
              for (const [, b] of buckets) {
                const l = (b as { label?: string }).label ?? ''
                labelKinds.set(l, (labelKinds.get(l) ?? new Set()).add(b.kind))
              }
              return [...buckets.entries()].map(([key, bucket]) => {
                const color = bucket.kind === 'client' ? '#008fa8' : bucket.kind === 'domain' ? '#7c3aed' : '#888'
                const label = (bucket as { label?: string }).label ?? ''
                const ambiguous = (labelKinds.get(label)?.size ?? 0) > 1
                const kindName = bucket.kind === 'client' ? 'client' : bucket.kind === 'domain' ? 'research domain' : 'chat session'
                return (
                  <Box key={key} sx={{ mb: 0.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, px: 2, py: 0.5 }} title={`${label} (${kindName})`}>
                      <FolderIcon sx={{ fontSize: 12, color }} />
                      <Typography sx={{ fontSize: '0.66rem', fontWeight: 700, color, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                        {label}{ambiguous ? ` · ${bucket.kind === 'client' ? 'client' : 'domain'}` : ''}
                      </Typography>
                    </Box>
                    {bucket.items.map(({ item, type }) => {
                      const meta = ARTIFACT_TYPE_META[type] ?? { label: type, icon: <AppsIcon sx={{ fontSize: 13 }} /> }
                      return (
                        <Box key={item.id} sx={{ px: 2, py: 0.6 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0, cursor: 'pointer' }}
                            onClick={() => onSelectSession(item.sessionId)} title="Open the source chat">
                            <Box sx={{ color: '#999', display: 'flex', flexShrink: 0 }}>{meta.icon}</Box>
                            <Typography sx={{ fontSize: '0.76rem', fontWeight: 500, color: '#2c2c2c', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {item.title}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', gap: 0.5, mt: 0.4, ml: 2.4, flexWrap: 'wrap' }}>
                            {item.versions.map(v => (
                              <Box key={v.version}
                                onClick={() => onOpenArtifactVersion(v)}
                                title={`Open ${item.title} v${v.version} (published ${formatDate(v.publishedAt)})`}
                                sx={{
                                  fontSize: '0.62rem', fontWeight: 700, px: 0.7, py: 0.1, borderRadius: '5px', cursor: 'pointer',
                                  color: '#0a6f82', background: '#f0fafc', border: '1px solid #bfe6ee',
                                  '&:hover': { background: '#008fa8', color: '#fff' },
                                }}>
                                v{v.version}
                              </Box>
                            ))}
                          </Box>
                        </Box>
                      )
                    })}
                  </Box>
                )
              })
            })()
          )}
        </SidebarSection>

        {/* Files */}
        <SidebarSection
          title="Files"
          icon={<FolderIcon sx={{ fontSize: 14 }} />}
          open={sectionsOpen.files}
          onToggle={() => toggleSection('files')}
          count={files.length}
        >
          {files.length === 0 ? (
            <EmptyHint text="No uploaded files" />
          ) : (
            files.slice(0, 20).map(f => {
              const name = f.key.split('/').pop() || f.key
              return (
                <SidebarItem
                  key={f.key}
                  label={truncate(name, 30)}
                  secondary={`${Math.round(f.size / 1024)}KB`}
                />
              )
            })
          )}
        </SidebarSection>

        {/* Agents — registry preview for admins/devs (PostHog 'reachy-agent-builder'
            flag, same gate as the Agent Builder on old Studio Ros). "Show all" opens
            the full-screen Agents overlay. */}
        {showAgents && (
          <SidebarSection
            title="Agents"
            icon={<SmartToyIcon sx={{ fontSize: 14 }} />}
            open={sectionsOpen.agents}
            onToggle={() => toggleSection('agents')}
            count={activeAgents.length}
          >
            {topAgents.length === 0 ? (
              <EmptyHint text="No active agents" />
            ) : (
              topAgents.map(a => {
                const MascotAvatar = getMascotByName(a.mascot).avatar
                return (
                  <SidebarItem
                    key={a.slug}
                    label={a.displayName}
                    secondary={a.shortDescription}
                    leading={<MascotAvatar size={22} />}
                    onClick={onOpenAgents}
                  />
                )
              })
            )}
            <Typography
              onClick={onOpenAgents}
              sx={{
                px: 2, py: 0.75, fontSize: '0.72rem', fontWeight: 600, color: '#6b8f5e',
                cursor: 'pointer', '&:hover': { textDecoration: 'underline' },
              }}
            >
              Show all →
            </Typography>
          </SidebarSection>
        )}

        {/* My Skills — skills the user created in Ros. The skills service is being
            built on a parallel track; actions unlock when it lands. */}
        <SidebarSection
          title="My Skills"
          icon={<ExtensionIcon sx={{ fontSize: 14 }} />}
          open={sectionsOpen.mySkills}
          onToggle={() => toggleSection('mySkills')}
        >
          <EmptyHint text="No skills yet" />
          <Box sx={{ display: 'flex', gap: 0.75, px: 2, pb: 1 }}>
            <SkillActionButton icon={<FileUploadIcon sx={{ fontSize: 13 }} />} label="Upload skill" />
            <SkillActionButton icon={<AddIcon sx={{ fontSize: 13 }} />} label="Create skill" />
          </Box>
        </SidebarSection>

        {/* Organization Skills — org-wide skills shared with everyone */}
        <SidebarSection
          title="Organization Skills"
          icon={<CorporateFareIcon sx={{ fontSize: 14 }} />}
          open={sectionsOpen.orgSkills}
          onToggle={() => toggleSection('orgSkills')}
        >
          <EmptyHint text="No organization skills yet" />
        </SidebarSection>
        </>}
      </Box>

      {/* Footer */}
      <Box sx={{
        px: collapsed ? 0 : 2, py: 1.5, borderTop: '1px solid #f0eeea',
        display: 'flex', alignItems: 'center', justifyContent: collapsed ? 'center' : 'flex-start', gap: 1,
      }}>
        <Tooltip title="Settings" placement={collapsed ? 'right' : 'top'}>
          <IconButton size="small" sx={{ color: '#999' }}>
            <SettingsIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
        {!collapsed && <Typography sx={{ fontSize: '0.72rem', color: '#999', flex: 1 }}>Settings</Typography>}
      </Box>
    </Box>
    </>
  )
}

// ── Sub-components ──────────────────────────────────────────────────

function SidebarSection({
  title, icon, open, onToggle, count, children,
}: {
  title: string; icon?: React.ReactNode; open: boolean; onToggle: () => void; count?: number; children: React.ReactNode
}) {
  return (
    <Box sx={{ borderBottom: '1px solid #f0eeea' }}>
      <Box
        onClick={onToggle}
        sx={{
          display: 'flex', alignItems: 'center', gap: 0.5,
          px: 1.5, py: 1, cursor: 'pointer',
          '&:hover': { background: '#faf9f5' },
        }}
      >
        {icon && <Box sx={{ color: '#999', display: 'flex' }}>{icon}</Box>}
        <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, color: '#999', textTransform: 'uppercase', letterSpacing: '0.05em', flex: 1 }}>
          {title}
        </Typography>
        {count !== undefined && count > 0 && (
          <Typography sx={{ fontSize: '0.62rem', color: '#999', mr: 0.5 }}>{count}</Typography>
        )}
        {open ? <ExpandLessIcon sx={{ fontSize: 14, color: '#ccc' }} /> : <ExpandMoreIcon sx={{ fontSize: 14, color: '#ccc' }} />}
      </Box>
      <Collapse in={open}>
        <Box sx={{ pb: 0.5 }}>{children}</Box>
      </Collapse>
    </Box>
  )
}

const ARTIFACT_TYPE_META: Record<string, { label: string; icon: ReactNode }> = {
  crosstabs: { label: 'Crosstabs', icon: <BarChartIcon sx={{ fontSize: 13 }} /> },
  website: { label: 'Deliverables', icon: <WebIcon sx={{ fontSize: 13 }} /> },
  survey: { label: 'Surveys', icon: <DescriptionIcon sx={{ fontSize: 13 }} /> },
}

/** Short panel marker (CT/MWD/ATH) — keyed on panelType, the same signal the resume uses. */
function panelMarker(panelType?: string): { label: string; color: string } | null {
  switch (panelType) {
    case 'crosstabs': return { label: 'CT', color: '#008fa8' }
    case 'site_builder': return { label: 'MWD', color: '#7c3aed' }
    case 'survey_review': return { label: 'ATH', color: '#c4873b' }
    default: return null
  }
}

function SidebarItem({
  label, secondary, active, disabled, onClick, panelType, trailing, leading,
}: {
  label: string; secondary?: string; active?: boolean; disabled?: boolean; onClick?: () => void; panelType?: string; trailing?: ReactNode; leading?: ReactNode
}) {
  const marker = panelMarker(panelType)
  return (
    <Box
      onClick={disabled ? undefined : onClick}
      sx={{
        px: 2, py: 0.6, cursor: disabled ? 'default' : 'pointer',
        background: active ? '#e8efe5' : 'transparent',
        opacity: disabled ? 0.5 : 1,
        '&:hover': disabled ? {} : { background: active ? '#e8efe5' : '#faf9f5' },
        borderLeft: active ? '3px solid #6b8f5e' : '3px solid transparent',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
        {leading && <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>{leading}</Box>}
        <Typography sx={{ fontSize: '0.78rem', fontWeight: active ? 600 : 400, color: '#2c2c2c', lineHeight: 1.3, flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {label}
        </Typography>
        {marker && (
          <Box sx={{
            flexShrink: 0, fontSize: '0.5rem', fontWeight: 700, letterSpacing: '0.04em',
            color: marker.color, background: `${marker.color}14`,
            px: 0.5, py: 0.125, borderRadius: '4px', lineHeight: 1.6,
          }}>
            {marker.label}
          </Box>
        )}
        {trailing && <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>{trailing}</Box>}
      </Box>
      {secondary && (
        <Typography sx={{ fontSize: '0.65rem', color: '#999', lineHeight: 1.3, mt: 0.2 }}>
          {secondary}
        </Typography>
      )}
    </Box>
  )
}

/** Skill library action — disabled until the skills service (parallel build) lands */
function SkillActionButton({ icon, label }: { icon: ReactNode; label: string }) {
  return (
    <Tooltip title="Skills are being built on a parallel track — coming soon">
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 0.5,
        px: 1, py: 0.4, borderRadius: '6px', border: '1px dashed #d8d5cc',
        color: '#aaa', fontSize: '0.68rem', fontWeight: 600, cursor: 'not-allowed',
        userSelect: 'none',
      }}>
        {icon}
        {label}
      </Box>
    </Tooltip>
  )
}

function EmptyHint({ text }: { text: string }) {
  return (
    <Typography sx={{ px: 2, py: 1, fontSize: '0.72rem', color: '#ccc', fontStyle: 'italic' }}>
      {text}
    </Typography>
  )
}
